import * as yup from 'yup';

import { CountriesList } from '@/shared/constants/countries-list';
import { FormLabelItem, TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';

import { AssetCategory } from '../../sj-orm/models/asset.dto';
import { LandDetails, LandGeneral } from '../../sj-orm/models/assets/categories/land';
import { PermittedUseType } from '../../sj-orm/models/assets/enums';
import { ContactType } from '../../sj-orm/models/contact.dto';
import { CollectionName } from '../../sj-orm/types';

import { assetsDocumentsBuilder, AssetsDocumentsPartType } from './builders/documents';
import { assetsOwnershipBuilder, AssetsOwnershipPartType } from './builders/ownership';
import { assetsProfitabilityBuilder } from './builders/profitability';
import { assetsStatisticsBuilder, AssetsStatisticsPartType } from './builders/statistics';
import { makeDtoFilter } from './filters/makeDtoFilter';
import { baseAssetFields } from './fields';

export type Land = LandFormGeneralType &
  LandFormDetailsType &
  Omit<AssetsOwnershipPartType, 'createdAt' | 'updatedAt'> &
  AssetsStatisticsPartType &
  Omit<AssetsDocumentsPartType, 'createdAt' | 'updatedAt'>;

const PermittedUseList = [
  {
    label: 'forms_assets:land.enums.useType.recreational',
    key: PermittedUseType.recreational,
  },
  {
    label: 'forms_assets:land.enums.useType.sanitary',
    key: PermittedUseType.sanitary,
  },
];

type LandHelpGeneral = {
  nickName: QuestionType.TEXT;
  country: QuestionType.SELECT_FROM_LIST;
  purchaseValue: QuestionType.CURRENCY_CALCULATOR;
};

type LandHelpDetails = {
  numberName?: QuestionType.TEXT;
  number?: QuestionType.TEXT;
  commencementDate?: QuestionType.DATE_TIME;
  permittedUse?: QuestionType.SELECT_FROM_LIST;
  landArea?: QuestionType.NUMBER;
  compositionOfObjects?: QuestionType.TEXT;
  buildingInformationOnTheLandPlot?: QuestionType.CHECKBOX;
  chooseBuilding?: QuestionType.PICK_ARRAY_OF_DTO;
  contacts?: QuestionType.PICK_ARRAY_OF_DTO;
  address?: QuestionType.ADDRESS;
  attachments?: QuestionType.FILE_UPLOAD;
};

type LandFormGeneralType = TDtoWrapper<LandGeneral, LandHelpGeneral>;
type LandFormDetailsType = TDtoWrapper<LandDetails, LandHelpDetails>;

const LandFormGeneral: LandFormGeneralType = {
  nickName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.nickname.label',
    value: '',
    required: true,
    yupScheme: yup.string().required('forms_assets:common.inputs.nickname.errors.required'),
    colSpan: 2,
  },
  country: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.country.label',
    tooltip: 'forms_assets:common.inputs.country.tooltip',
    value: '',
    required: true,
    items: CountriesList,
    withSearch: true,
  },
  purchaseValue: {
    type: QuestionType.CURRENCY_CALCULATOR,
    label: 'forms_assets:land.inputs.purchaseValue.label',
    tooltip: 'forms_assets:land.inputs.purchaseValue.tooltip',
    equivalentValueTooltip: 'forms_assets:common.inputs.equivalentValue.tooltip',
    value: 0,
    required: true,
  },
};

const LandFormDetails: LandFormDetailsType = {
  numberLabel: {
    type: FormLabelItem.label,
    label: 'forms_assets:land.inputs.numberLabel.label',
    order: 1,
  },
  numberName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:land.inputs.numberName.label',
    order: 2,
  },
  number: {
    type: QuestionType.TEXT,
    label: 'forms_assets:land.inputs.number.label',
    order: 3,
  },
  commencementDate: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:land.inputs.commencementDate.label',
    tooltip: 'forms_assets:land.inputs.commencementDate.tooltip',
    order: 4,
  },
  permittedUse: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:land.inputs.permittedUse.label',
    items: PermittedUseList,
    order: 5,
  },
  landArea: {
    type: QuestionType.NUMBER,
    label: 'forms_assets:land.inputs.landArea.label',
    order: 6,
  },
  compositionOfObjects: {
    type: QuestionType.TEXT,
    label: 'forms_assets:land.inputs.compositionOfObjects.label',
    order: 7,
  },
  buildingInformationOnTheLandPlot: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:land.inputs.buildingInformationOnTheLandPlot.label',
    order: 8,
    colSpan: 2,
  },
  chooseBuilding: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:land.inputs.chooseBuilding.label',
    items: CollectionName.ASSETS,
    mode: 'multiple',
    showIf: {
      buildingInformationOnTheLandPlot: {
        value: true,
      },
    },
    makeExtFilter: makeDtoFilter({
      categories: [AssetCategory.RESIDENTIAL_REAL_ESTATE, AssetCategory.COMMERCIAL_REAL_ESTATE],
      withCountryFilter: true,
    }),
    dtoType: [AssetCategory.RESIDENTIAL_REAL_ESTATE, AssetCategory.COMMERCIAL_REAL_ESTATE],
    deps: ['country'],
    order: 9,
  },
  contacts: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:common.inputs.contacts.label',
    items: CollectionName.CONTACTS,
    mode: 'multiple',
    order: 9,
    dtoType: [
      ContactType.INDIVIDUAL,
      ContactType.LEGAL_ENTITY,
      ContactType.BROKER,
      ContactType.BANK,
    ],
  },
  address: {
    type: QuestionType.ADDRESS,
    label: 'forms_assets:common.inputs.address.label',
    tooltip: 'forms_assets:common.inputs.address.tooltip',
    value: undefined,
    order: 10,
  },
  addressComment: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_assets:common.inputs.addressComment.label',
    value: '',
    order: 11,
  },
  attachments: baseAssetFields.attachments,
};

const LandForm = [
  LandFormGeneral,
  LandFormDetails,
  assetsOwnershipBuilder().form,
  assetsProfitabilityBuilder([]),
  assetsStatisticsBuilder([
    'taxAgent',
    'taxAgentOther',
    'taxAgentWithBroker',
    'taxAgentWithBrokerOther',
    'legalTaxAgent',
    'legalTaxAgentOther',
    'holderTaxAgent',
    'holderTaxAgentOther',
    'publicFundTaxAgent',
    'publicTaxAgentOther',
    'profitabilityFact',
    'profitabilityPlan',
  ]).form,
  assetsDocumentsBuilder(),
];

export { LandForm };
